// src/components/Step1/Step1.jsx
import React, { useEffect, useState } from 'react'
import { useMutation } from 'react-query'
import { sendEmailVerification, validateRecaptcha } from '../../../Api' // Adjust the import path as needed
import styles from './styles.module.css'
import DatafinitiLogo from '../../HomeDR12/images/datafiniti-logo-white.svg'
import jwtDecode from 'jwt-decode'
import { useLocation } from 'react-router-dom'
import CheckIcon from '@material-ui/icons/Check' // You can use any icon you prefer
import useAlerts from '../../Snackbar/NewImplementations/HooksAlerts/useAlerts'
import ColorAlert from '../../Snackbar/NewImplementations/ColorAlert/ColorAlert'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'

function Step1 ({ isActive }) {
  const location = useLocation()
  const [email, setEmail] = useState('')
  const [isEmailValid, setIsEmailValid] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()

  // Use the shared alerts hook
  const { alerts, closeAlert, pushAlert } = useAlerts()

  const [mutate] = useMutation(sendEmailVerification)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const token = searchParams.get('token')

    if (token) {
      try {
        const decodedToken = jwtDecode(token)
        if (decodedToken && decodedToken.sub) {
          setEmail(decodedToken.sub)
          setIsEmailSent(true)
          setIsCheckboxChecked(true)
          // Show a success notification immediately if email was decoded from token
          //   pushAlert({
          //     type: 'info',
          //     message: 'Email already verified via token.'
          //   })
        }
      } catch (error) {
        pushAlert({ type: 'error', message: 'Invalid token.' })
        console.error('Invalid token:', error)
      }
    }
  }, [location, pushAlert])

  const handleEmailChange = e => {
    const emailInput = e.target.value
    setEmail(emailInput)
    setIsEmailSent(false)

    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,15}$/
    setIsEmailValid(emailPattern.test(emailInput))
  }

  const handleCheckboxChange = e => {
    setIsCheckboxChecked(e.target.checked)
  }

  const emailSending = async e => {
    e.preventDefault()
    setIsLoading(true)
    const RECAPTCHA_THRESHOLD = 0.5 // reject any score below this

    /* 1️⃣ –– Get a v3 token from Google */
    if (!executeRecaptcha) {
      pushAlert({ type: 'error', message: 'reCAPTCHA not ready, try again.' })
      setIsLoading(false)
      return
    }

    let recaptchaToken = ''

    try {
      recaptchaToken = await executeRecaptcha('signup_email') // action name is arbitrary
    } catch {
      pushAlert({ type: 'error', message: 'Unable to execute reCAPTCHA.' })
      setIsLoading(false)
      return
    }

    /* 2️⃣ –– Ask your server to validate the token */
    try {
      const { status: capStatus, data: capData } = await validateRecaptcha(
        recaptchaToken
      )
      const { success, score } = capData
      if (capStatus >= 400 || !success || score < RECAPTCHA_THRESHOLD) {
        pushAlert({ type: 'error', message: 'reCAPTCHA failed, please retry.' })
        setIsLoading(false)
        return
      }
    } catch {
      pushAlert({ type: 'error', message: 'reCAPTCHA validation error.' })
      setIsLoading(false)
      return
    }

    /* 3️⃣ –– Token is good → send sign-up email */
    try {
      const { status, data } = await mutate(email)

      if (status < 400) {
        setIsEmailSent(true)
        pushAlert({
          type: 'info',
          message: 'Check your email to verify your information.'
        })
      } else {
        const errMsg = data?.errors || 'An error occurred while sending email.'
        pushAlert({ type: 'error', message: errMsg })
      }
    } catch (error) {
      const errMsg = error?.message || 'An error occurred while sending email.'
      pushAlert({ type: 'error', message: errMsg })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <section className={styles.background}>
      <div className={styles.mainContainer}>
        <img
          src={DatafinitiLogo}
          alt='DatafinitiLogo'
          className={styles.logo}
          style={{ opacity: isActive ? 1 : 0 }}
        />
        <div>
          <h1 className={styles.step1Title}>Step 1: Email</h1>
          <span className={styles.provideEmailSpan}>
            Provide email and validate.
          </span>
          <form
            onSubmit={emailSending}
          >
            <input
              autoFocus
              autoComplete='off'
              type='text'
              className={styles.inputEmailStep1}
              placeholder='Email'
              value={email}
              onChange={handleEmailChange}
              disabled={!isActive}
            />
            <div className={styles.checkboxContainer}>
              <input
                type='checkbox'
                id='terms'
                className={styles.checkbox}
                disabled={!isActive}
                onChange={handleCheckboxChange}
                checked={isCheckboxChecked}
              />
              <label htmlFor='terms' className={styles.checkboxLabel}>
                I agree to the{' '}
                <a
                  href='https://datafiniti.co/terms/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={`${styles.policy} ${
                    !isActive ? styles.disabled : ''
                  }`}
                  onClick={e => !isActive && e.preventDefault()}
                >
                  Terms of Use
                </a>{' '}
                and{' '}
                <a
                  href='https://datafiniti.co/privacy-policy/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className={`${styles.policy} ${
                    !isActive ? styles.disabled : ''
                  }`}
                  onClick={e => !isActive && e.preventDefault()}
                >
                  Privacy Policy
                </a>
              </label>
            </div>
            <button
              type='submit'
              className={`${styles.buttonStep1} ${
                isEmailSent
                  ? styles.buttonSuccess
                  : isEmailValid && isCheckboxChecked
                  ? styles.buttonEnabled
                  : styles.buttonDisabled
              }`}
              disabled={
                !isEmailValid ||
                isLoading ||
                isEmailSent ||
                !isActive ||
                !isCheckboxChecked
              }
            >
              {isEmailSent ? (
                <CheckIcon className={styles.iconInsideButton} />
              ) : isLoading ? (
                'Sending...'
              ) : (
                'Continue'
              )}
            </button>
          </form>

          <div style={{ width: '350px' }}>
            <p style={{ fontSize: 12, marginTop: 16 }}>
              This site is protected by reCAPTCHA and the&nbsp;
              <a
                href='https://policies.google.com/privacy'
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: 'white' }}
              >
                Privacy&nbsp;Policy
              </a>{' '}
              and&nbsp;
              <a
                href='https://policies.google.com/terms'
                target='_blank'
                rel='noopener noreferrer'
                style={{ color: 'white' }}
              >
                Terms&nbsp;of&nbsp;Service
              </a>{' '}
              apply.
            </p>
          </div>
        </div>
      </div>

      {/* Render stacked alerts */}
      {alerts.map((alert, i) => (
        <ColorAlert
          key={alert.id}
          open={alert.open}
          type={alert.type}
          label={alert.message}
          onClose={() => closeAlert(alert.id)}
          style={{
            position: 'fixed',
            left: '12.5%',
            transform: 'translateX(-50%)',
            top: `calc(126.1% - ${20 + i * 80}px - var(--alert-offset))`
            // top: `calc(126.1% - ${20 + i * 80}px)`
          }}
        />
      ))}
    </section>
  )
}

export default Step1
